import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GITInvestigator() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [passage, setPassage] = useState('');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated()) navigate('/login');
  }, [isAuthenticated, navigate]);

  // Fetch history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/git-investigator/history`, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setHistory(data.investigations || []);
        }
      } catch { /* silent */ }
    };
    fetchHistory();
  }, [token]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInvestigate = async () => {
    if (!passage.trim()) return;
    setLoading(true);
    setMessages([{ role: 'user', content: `Investigate: **${passage}**${question ? `\n\nQuestion: ${question}` : ''}` }]);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/git-investigator/investigate`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ passage, question: question || null }),
      });
      const data = await res.json();
      if (data.error) {
        setMessages(prev => [...prev, { role: 'error', content: data.error }]);
      } else {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: 'Investigation failed. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || !sessionId) return;
    const msg = followUp;
    setFollowUp('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/git-investigator/follow-up`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const data = await res.json();
      if (data.error) {
        setMessages(prev => [...prev, { role: 'error', content: data.error }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'error', content: 'Follow-up failed.' }]);
    }
    setLoading(false);
  };

  const loadInvestigation = async (sid) => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/git-investigator/history/${sid}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setPassage(data.passage || '');
        setMessages(data.messages || []);
        setShowHistory(false);
      }
    } catch { /* silent */ }
  };

  const startNew = () => {
    setPassage(''); setQuestion(''); setMessages([]); setSessionId(null); setFollowUp('');
  };

  const SUGGESTED_PASSAGES = [
    '2 Thessalonians 2:1-3',
    'Matthew 24:29-31',
    '1 Corinthians 15:51-52',
    'Revelation 3:10',
    '1 Thessalonians 5:9',
    'Daniel 9:27',
  ];

  return (
    <div className="min-h-screen" style={{ background: '#060503' }}>
      <Header />

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-[9px] tracking-[0.4em] uppercase font-mono text-amber-400/80 mb-2">Grammar Integrity Test</p>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold mb-3" style={{
            background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            GIT Investigator
          </h1>
          <p className="text-sm text-amber-300/70 font-serif max-w-lg mx-auto">
            Investigate any biblical passage through the lens of grammar, context, and cross-reference. Follow the text wherever it leads.
          </p>
        </div>

        <div className="flex gap-4">
          {/* History Sidebar (desktop) */}
          <div className={`${showHistory ? 'block' : 'hidden'} lg:block w-full lg:w-64 flex-shrink-0`}>
            <div className="rounded-xl border p-3" style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.1)' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-400/70">Past Investigations</p>
                <button onClick={startNew} data-testid="btn-new-investigation"
                  className="text-[10px] text-amber-500 hover:text-amber-300 font-mono">+ New</button>
              </div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {history.length === 0 && (
                  <p className="text-[10px] text-amber-600/50 font-serif text-center py-4">No investigations yet</p>
                )}
                {history.map(inv => (
                  <button key={inv.session_id} onClick={() => loadInvestigation(inv.session_id)}
                    data-testid={`history-${inv.session_id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                      sessionId === inv.session_id ? 'bg-amber-900/15' : 'hover:bg-amber-900/8'
                    }`}>
                    <p className="text-amber-300/70 font-serif truncate">{inv.passage}</p>
                    <p className="text-[9px] text-amber-500/50 font-mono mt-0.5">
                      {new Date(inv.created_at).toLocaleDateString()}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Area */}
          <div className="flex-1 min-w-0">
            {messages.length === 0 ? (
              /* Initial Investigation Form */
              <div className="rounded-xl border p-6" data-testid="git-form"
                style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.1)' }}>
                <div className="mb-5">
                  <label className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-400/70 mb-2 block">
                    Biblical Passage
                  </label>
                  <input
                    type="text"
                    value={passage}
                    onChange={e => setPassage(e.target.value)}
                    placeholder="e.g., Matthew 24:29-31"
                    data-testid="input-passage"
                    className="w-full px-4 py-3 rounded-lg border text-sm font-serif text-amber-100 placeholder:text-amber-600/40 focus:outline-none focus:border-amber-600/40"
                    style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.2)' }}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleInvestigate()}
                  />
                </div>

                <div className="mb-5">
                  <label className="text-[9px] tracking-[0.2em] uppercase font-mono text-amber-400/70 mb-2 block">
                    Specific Question (Optional)
                  </label>
                  <input
                    type="text"
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    placeholder="e.g., Does this passage teach a pre-tribulation rapture?"
                    data-testid="input-question"
                    className="w-full px-4 py-3 rounded-lg border text-sm font-serif text-amber-100 placeholder:text-amber-600/40 focus:outline-none focus:border-amber-600/40"
                    style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.2)' }}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleInvestigate()}
                  />
                </div>

                <button onClick={handleInvestigate} disabled={loading || !passage.trim()} data-testid="btn-investigate"
                  className="w-full px-6 py-3 rounded-lg text-sm font-serif font-bold transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
                  style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff', boxShadow: '0 4px 15px rgba(217,119,6,0.3)' }}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Investigating...
                    </span>
                  ) : 'Begin Investigation'}
                </button>

                {/* Suggested Passages */}
                <div className="mt-6">
                  <p className="text-[9px] tracking-[0.15em] uppercase font-mono text-amber-500/60 mb-2">Suggested Passages</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_PASSAGES.map(p => (
                      <button key={p} onClick={() => setPassage(p)}
                        className="px-3 py-1.5 rounded-full text-[10px] font-serif transition-colors border hover:border-amber-700/40"
                        style={{ background: 'rgba(20,15,8,0.4)', borderColor: 'rgba(120,80,20,0.25)', color: 'rgba(217,170,100,0.75)' }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Conversation View */
              <div className="space-y-4">
                {/* Messages */}
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2" data-testid="git-messages">
                  {messages.map((msg, i) => (
                    <div key={i} className={`rounded-xl p-4 border ${
                      msg.role === 'user' ? '' : msg.role === 'error' ? '' : ''
                    }`} style={{
                      background: msg.role === 'user'
                        ? 'rgba(217,119,6,0.06)' : msg.role === 'error'
                        ? 'rgba(239,68,68,0.06)' : 'rgba(15,10,5,0.6)',
                      borderColor: msg.role === 'user'
                        ? 'rgba(217,119,6,0.15)' : msg.role === 'error'
                        ? 'rgba(239,68,68,0.15)' : 'rgba(120,80,20,0.1)',
                    }}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                          style={{
                            background: msg.role === 'user' ? 'rgba(217,119,6,0.2)' : msg.role === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
                            color: msg.role === 'user' ? '#d97706' : msg.role === 'error' ? '#ef4444' : '#10b981',
                          }}>
                          {msg.role === 'user' ? 'Y' : msg.role === 'error' ? '!' : 'G'}
                        </div>
                        <span className="text-[9px] font-mono tracking-wider uppercase" style={{
                          color: msg.role === 'user' ? 'rgba(217,119,6,0.5)' : msg.role === 'error' ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)',
                        }}>
                          {msg.role === 'user' ? 'You' : msg.role === 'error' ? 'Error' : 'GIT Investigator'}
                        </span>
                      </div>
                      <div className="text-sm font-serif leading-relaxed whitespace-pre-wrap" style={{
                        color: msg.role === 'error' ? 'rgba(239,68,68,0.8)' : 'rgba(230,190,130,0.9)',
                      }}>
                        {formatGITResponse(msg.content)}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="rounded-xl p-4 border" style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.1)' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs font-serif text-amber-400/70 italic animate-pulse">The GIT Investigator is examining the grammar...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Follow-up Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={followUp}
                    onChange={e => setFollowUp(e.target.value)}
                    placeholder="Ask a follow-up question..."
                    data-testid="input-followup"
                    className="flex-1 px-4 py-3 rounded-lg border text-sm font-serif text-amber-100 placeholder:text-amber-600/40 focus:outline-none focus:border-amber-600/40"
                    style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.2)' }}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleFollowUp()}
                    disabled={loading}
                  />
                  <button onClick={handleFollowUp} disabled={loading || !followUp.trim()} data-testid="btn-followup"
                    className="px-5 py-3 rounded-lg text-sm font-serif font-bold transition-all hover:scale-105 disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff' }}>
                    Ask
                  </button>
                </div>

                {/* New Investigation Button */}
                <button onClick={startNew} data-testid="btn-start-new"
                  className="w-full py-2 rounded-lg text-xs font-serif transition-colors border hover:bg-amber-900/10"
                  style={{ borderColor: 'rgba(120,80,20,0.2)', color: 'rgba(217,170,100,0.65)' }}>
                  Start New Investigation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function formatGITResponse(text) {
  if (!text) return '';
  // Simple formatting: bold **text**, headers with emojis
  return text.split('\n').map((line, i) => {
    const trimmed = line.trim();
    // Section headers
    if (trimmed.startsWith('📖') || trimmed.startsWith('🔤') || trimmed.startsWith('🔗') || trimmed.startsWith('⚖️') || trimmed.startsWith('📝')) {
      return <p key={i} className="text-amber-300/90 font-semibold mt-3 mb-1 text-sm">{trimmed}</p>;
    }
    // Bold markers
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return <p key={i} className="text-amber-200/80 font-semibold mt-2 mb-0.5">{trimmed.replace(/\*\*/g, '')}</p>;
    }
    // Verdict line
    if (trimmed.includes('SOUND') || trimmed.includes('CONSISTENT') || trimmed.includes('NEUTRAL') || trimmed.includes('CONTESTED') || trimmed.includes('UNCLEAR')) {
      if (trimmed.includes('VERDICT') || trimmed.includes('⚖️')) {
        const verdictColor = trimmed.includes('SOUND') ? '#10b981' : trimmed.includes('CONSISTENT') ? '#06b6d4' : '#eab308';
        return <p key={i} className="font-bold mt-3 mb-1" style={{ color: verdictColor }}>{trimmed}</p>;
      }
    }
    // Empty line
    if (!trimmed) return <div key={i} className="h-2" />;
    // Regular line
    return <p key={i} className="mb-1">{trimmed}</p>;
  });
}
